import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer
} from 'recharts';
import { Building2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLocation } from 'wouter';

interface TopClientsSpendChartProps {
  data?: Array<{
    clientId: number;
    name: string;
    spend: number;
  }>;
  loading: boolean;
  className?: string;
}

export default function TopClientsSpendChart({ data, loading, className }: TopClientsSpendChartProps) {
  const [, setLocation] = useLocation();

  if (loading) {
    return (
      <Card className={cn("athlyn-card", className)}>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Top 10 Clients by Spend</span>
            <Building2 className="w-5 h-5 text-muted-foreground" />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[400px] w-full" />
        </CardContent>
      </Card>
    );
  }

  const chartData = data || [];
  
  // Format the data and sort by spend
  const formattedData = chartData
    .map(item => ({
      ...item,
      spendK: item.spend / 1000, // Convert to thousands
      displayName: item.name.length > 20 ? item.name.substring(0, 20) + '...' : item.name
    }))
    .sort((a, b) => b.spend - a.spend)
    .slice(0, 10); // Take only top 10

  const handleBarClick = (data: any) => {
    if (data && data.clientId) {
      setLocation(`/clients/${data.clientId}`);
    }
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background border border-border rounded-lg p-3 shadow-md">
          <p className="font-medium">{label}</p>
          <p className="text-sm text-muted-foreground">
            Total Spend: <span className="font-medium text-foreground">AED {payload[0].value.toFixed(1)}k</span>
          </p>
          <p className="text-xs text-muted-foreground mt-1">Click to view client details</p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card className={cn("athlyn-card", className)}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Top 10 Clients by Spend</span>
          <Building2 className="w-5 h-5 text-muted-foreground" />
        </CardTitle>
      </CardHeader>
      <CardContent>
        {formattedData.length === 0 ? (
          <div className="h-[400px] flex items-center justify-center text-muted-foreground">
            No client spending data available
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart 
              data={formattedData} 
              layout="horizontal"
              margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
            >
              <CartesianGrid 
                strokeDasharray="3 3" 
                className="stroke-muted" 
              />
              <XAxis 
                type="number"
                className="text-xs"
                tick={{ fill: 'hsl(var(--muted-foreground))' }}
                tickFormatter={(value) => `${value}k`}
              />
              <YAxis 
                type="category"
                dataKey="displayName"
                className="text-xs"
                tick={{ fill: 'hsl(var(--muted-foreground))' }}
                width={90}
              />
              <Tooltip 
                content={<CustomTooltip />}
                cursor={{ fill: 'hsl(var(--muted))', opacity: 0.2 }}
              />
              <Bar 
                dataKey="spendK" 
                fill="hsl(var(--primary))"
                radius={[0, 4, 4, 0]}
                onClick={handleBarClick}
                style={{ cursor: 'pointer' }}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}